import { ReactElement, useState, createContext, useContext, useMemo, useCallback } from "react";
import { Box } from "@mui/material";
import { LAYOUT_CONFIG, LayoutMode } from "../../constants/layout";
import Header from "./components/Header/Header";
import Sidebar from "./components/Menu/Sidebar";
import HorizontalNavbar from "./components/Menu/HorizontalNavbar";

interface LayoutContextType {
  layoutMode: LayoutMode;
  setLayoutMode: (mode: LayoutMode) => void;
  isMenuOpen: boolean;
  toggleMenu: () => void;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

const getStoredSidebarType = (): 'default' | 'icon' => {
  const legacySidebarType = localStorage.getItem('sidebarType');
  if (legacySidebarType === 'icon' || legacySidebarType === 'default') {
    return legacySidebarType;
  }

  try {
    const savedPreferences = localStorage.getItem('layoutPreferences');
    const sidebarType = savedPreferences ? JSON.parse(savedPreferences).sidebarType : null;
    return sidebarType === 'icon' ? 'icon' : 'default';
  } catch {
    return 'default';
  }
};

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) throw new Error('useLayout must be used within LayoutProvider');
  return context;
};

interface LayoutProps {
  children: ReactElement;
  mode?: LayoutMode;
}

export default function Layout({ children, mode = 'vertical' }: LayoutProps) {
  const [layoutMode, setLayoutMode] = useState<LayoutMode>(() => 
    (localStorage.getItem('layoutMode') as LayoutMode) || mode
  );
  const [isMenuOpen, setIsMenuOpen] = useState(() => getStoredSidebarType() !== 'icon');

  const handleToggle = useCallback(() => {
    setIsMenuOpen(prev => !prev);
    const isMobile = window.screen.width <= 998;
    document.body.classList.toggle("sidebar-enable");
    if (!isMobile) document.body.classList.toggle("vertical-collpsed");
  }, []);

  const handleLayoutModeChange = useCallback((newMode: LayoutMode) => {
    setLayoutMode(newMode);
    localStorage.setItem('layoutMode', newMode);
    if (newMode === 'vertical') {
      setIsMenuOpen(getStoredSidebarType() !== 'icon');
    }
  }, []);

  const contextValue: LayoutContextType = useMemo(() => ({
    layoutMode,
    setLayoutMode: handleLayoutModeChange,
    isMenuOpen,
    toggleMenu: handleToggle,
  }), [layoutMode, handleLayoutModeChange, isMenuOpen, handleToggle]);

  const isHorizontal = layoutMode === 'horizontal';
  const topMargin = isHorizontal 
    ? LAYOUT_CONFIG.headerHeight + LAYOUT_CONFIG.horizontalMenuHeight 
    : LAYOUT_CONFIG.headerHeight;

  const mainStyles = {
    flexGrow: 1,
    p: 0,
    backgroundColor: "#f3f3f9",
    marginTop: `${topMargin}px`,
    height: `calc(100vh - ${topMargin}px)`,
    overflow: 'auto',
    // Without this, a page whose content height crosses the scroll threshold (e.g. switching the
    // ciblage builder from "simple" to "avancé", which is much shorter) shows/hides the vertical
    // scrollbar, changing the available width and visibly shifting every element sideways —
    // reserving the gutter keeps the content width constant whether or not scrolling is needed.
    scrollbarGutter: 'stable',
    ...(layoutMode === 'vertical' && {
      transition: LAYOUT_CONFIG.transition,
      width: `calc(100% - ${isMenuOpen ? LAYOUT_CONFIG.drawerWidth : LAYOUT_CONFIG.collapsedWidth}px)`,
    }),
  };

  return (
    <LayoutContext.Provider value={contextValue}>
      <Box sx={{ display: "flex", flexDirection: isHorizontal ? "column" : "row", minHeight: "100vh" }}>
        <Header 
          open={isMenuOpen} 
          onToggle={handleToggle} 
          layoutMode={layoutMode}
          onLayoutModeChange={handleLayoutModeChange}
        />
        {layoutMode === 'vertical' && <Sidebar open={isMenuOpen} />}
        {layoutMode === 'horizontal' && <HorizontalNavbar />}
        <Box component="main" sx={mainStyles}>
          {children}
        </Box>
      </Box>
    </LayoutContext.Provider>
  );
}
